import React, { useState, useRef, useEffect } from 'react';
import { Button } from './button';
import { X, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useAuth } from '@soap/modules';
import Compressor from 'compressorjs';
import { getPrimaryFunctionAppRoot } from '../utils/functionAppRoot';

interface BlobMeta {
  id: string;
  name: string;
}

interface ImageUploadProps {
  value: BlobMeta | null;
  onChange: (blobMeta: BlobMeta | null) => void;
  acceptedTypes?: string;
  maxBytes?: number;
  disabled?: boolean;
}

export function ImageUpload({ 
  value, 
  onChange, 
  acceptedTypes = '.jpg,.jpeg,.jfif,.png,.webp',
  maxBytes = 5000000, // 5MB default
  disabled = false 
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { idToken, authReady } = useAuth('image-upload');

  // Fetch existing image preview when value changes
  useEffect(() => {
    let cancelled = false;
    
    if (value?.id && authReady) {
      // Fetch the existing image
      setLoading(true);
      const fetchImage = async () => {
        const startTime = Date.now();
        try {
          const endpoint = `${getPrimaryFunctionAppRoot()}/GetBlob`;
          const url = `${endpoint}?id=${encodeURI(value.id)}&it=${idToken}`;
          const response = await fetch(url);
          if (response.ok && !cancelled) {
            const blob = await response.blob();
            const previewUrl = URL.createObjectURL(blob);
            setPreview(previewUrl);
          }
        } catch (err) {
          if (!cancelled) {
            console.error('Failed to load existing image:', err);
          }
        } finally {
          if (!cancelled) {
            // Ensure loader shows for at least 500ms
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 500 - elapsed);
            await new Promise(resolve => setTimeout(resolve, remaining));
            if (!cancelled) {
              setLoading(false);
            }
          }
        }
      };
      fetchImage();
    } else if (!value) {
      // Clear preview when value is removed
      setLoading(false);
      setPreview((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
    }
    
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [value?.id, authReady, idToken]);


  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset error
    setError(null);

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/jfif'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, or WEBP)');
      return;
    }

    // Validate file size
    if (maxBytes && file.size > maxBytes) {
      setError(`File size must be less than ${Math.round(maxBytes / 1000000)}MB`);
      return;
    }

    setUploading(true);

    try {
      // Compress image
      const compressedBlob = await compressImage(file);
      
      // Generate preview
      const previewUrl = URL.createObjectURL(compressedBlob);
      setPreview(previewUrl);

      // Generate unique ID
      const id = crypto.randomUUID();
      
      // Upload to backend
      await uploadToBackend(id, compressedBlob);

      // Update parent with BlobMeta
      onChange({ id, name: file.name });
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Upload failed. Please try again.');
      setPreview(null);
    } finally {
      setUploading(false);
      // Clear the file input so the same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const compressImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        checkOrientation: false,
        maxWidth: 1024,
        maxHeight: 768,
        success: resolve,
        error: reject,
      });
    });
  };

  const uploadToBackend = async (id: string, blob: Blob) => {
    if (!authReady) {
      throw new Error('Authentication not ready');
    }

    const endpoint = `${getPrimaryFunctionAppRoot()}/AddBlob`;
    const response = await fetch(`${endpoint}?id=${id}&it=${idToken}`, {
      method: 'POST',
      body: blob,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }
  };

  const handleRemove = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    onChange(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        onChange={handleFileSelect}
        disabled={disabled || uploading}
        className="hidden"
      />

      {!value && !preview ? (
        <Button
          type="button"
          variant="outline"
          onClick={handleButtonClick}
          disabled={disabled || uploading}
          className="w-full"
        >
          <Upload size={16} className="mr-2" />
          {uploading ? 'Uploading...' : 'Choose Image'}
        </Button>
      ) : (
        <div className="flex items-center gap-3 p-3 border rounded-md bg-gray-50">
          <ImageIcon size={20} className="text-gray-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{value?.name}</p>
            {loading ? (
              <div className="mt-2 flex items-center justify-center w-[200px] h-[80px] border rounded bg-white">
                <Loader2 size={24} className="text-gray-400 animate-spin" />
              </div>
            ) : preview ? (
              <img 
                src={preview} 
                alt="Preview" 
                className="mt-2 max-w-[200px] max-h-[150px] rounded border"
              />
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={disabled || loading}
          >
            <X size={16} />
          </Button>
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive-action">{error}</p>
      )}
    </div>
  );
}

